import React, { Suspense } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Routes } from "@datadog/browser-rum-react/react-router-v7";
import { Navigate, Route, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import { AuthCallback } from "./components/AuthCallback";
import { Layout } from "./components/Layout";
import { RoleRoute } from "./components/RoleRoute";
import {
  canAccessOperationalAreas,
  canManageInvoicePayments,
  canManageUsers,
  canViewClientProfile,
  canViewInvoices,
  canViewOperationalAreas,
  canViewPolicyDetail,
  canViewReports,
  getDefaultLandingPath,
} from "./lib/permissions";
import type { UserRole } from "./types";
import { IdleSessionWatcher } from "./components/IdleSessionWatcher";
import { Toaster } from "./components/ui/sonner";
import {
  AdviserDetailView,
  AdviserPerformanceReport,
  AppConfigPage,
  AssetDetail,
  AuditLogView,
  BordeauxReport,
  BusinessAdminDashboard,
  BusinessAdminDetailView,
  BusinessWrittenReportView,
  CashCreditsReportView,
  ClientProfile,
  ClientSearch,
  CombinedQuoteBuilder,
  CommissionsReportView,
  CreateFAP,
  CreateOrganisation,
  CreatePerson,
  Dashboard,
  DebitorsReport,
  EditFAP,
  EOfferDemo,
  FAPDetail,
  FinanceAdminDashboard,
  HealthPage,
  InvoiceDetail,
  InvoicesView,
  MTAWorkflow,
  NotificationsPage,
  PoliciesView,
  PolicyDetail,
  PolicyQuoteHistory,
  PolicyRenewalReportView,
  PublicEOfferPage,
  PublicEOfferReturnPage,
  QuotesPipeline,
  ReconciliationHistoryView,
  RenewalAmendment,
  RenewalDetail,
  RenewalsView,
  ReportsView,
  RouteFallback,
  UserDetail,
  UsersView,
} from "./lazyRoutes";
import { routes } from "./routes";

const DashboardRoute: React.FC = () => {
  const { currentUser } = useApp();

  if (!currentUser) return <Dashboard />;

  const role = currentUser.role as UserRole;
  if (role === "FINANCE_ADMIN") return <FinanceAdminDashboard />;
  if (role === "BUSINESS_ADMIN" || role === "SUPER_ADMIN") {
    return <BusinessAdminDashboard />;
  }
  return <Dashboard />;
};

const AppContent: React.FC = () => {
  const { currentUser } = useApp();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const role = currentUser?.role as UserRole | undefined;
  const operational = canAccessOperationalAreas(role);
  const operationalView = canViewOperationalAreas(role);
  const clientProfile = canViewClientProfile(role);
  const policyDetail = canViewPolicyDetail(role);
  const reports = canViewReports(role);
  const invoices = canViewInvoices(currentUser ?? undefined);
  const invoicePayments = canManageInvoicePayments(role);
  const userManagement = canManageUsers(role);
  const roleDeniedRedirect = getDefaultLandingPath(role);

  // Check if we're on a public route (no auth required)
  const isPublicRoute = location.pathname.startsWith("/eoffer/");

  // Check if we're on the callback route (Auth0 redirect)
  const isCallback =
    location.pathname === routes.callback ||
    location.search.includes("code=") ||
    location.search.includes("error=") ||
    location.hash.includes("code=") ||
    location.hash.includes("error=");

  // Keep hook order stable across route transitions.
  React.useEffect(() => {
    if (isCallback || isPublicRoute || isLoading || isAuthenticated || currentUser) {
      return;
    }

    // Store the current path to return to after login
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const returnTo = currentPath !== "/" ? currentPath : undefined;

    // Redirect to Auth0 login page
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'login',
      },
      appState: {
        returnTo: returnTo || routes.dashboard,
      },
    });
  }, [isCallback, isLoading, isAuthenticated, currentUser, loginWithRedirect, location, isPublicRoute]);

  // Show callback handler if we're processing Auth0 callback
  if (isCallback) {
    return <AuthCallback />;
  }

  // For ADVISER users, adviserRole is fetched asynchronously after Auth0 sets currentUser.
  // Block route rendering until it resolves so RoleRoute permission checks are accurate.
  const adviserProfilePending = role === 'ADVISER' && currentUser?.adviserRole === undefined;

  // Wait for Auth0 and for AppContext to map the user (role) before route guards run.
  if (!isPublicRoute && (isLoading || !currentUser || adviserProfilePending)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // For public routes, render without Layout
  if (isPublicRoute) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/eoffer/:eofferId" element={<PublicEOfferPage />} />
          <Route path="/eoffer/:eofferId/return" element={<PublicEOfferReturnPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to={routes.dashboard} replace />} />
          <Route path={routes.dashboard} element={<DashboardRoute />} />

          <Route
            path={routes.clients}
            element={
              <RoleRoute allow={operationalView}>
                <ClientSearch />
              </RoleRoute>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <RoleRoute allow={clientProfile} redirectTo={roleDeniedRedirect}>
                <ClientProfile />
              </RoleRoute>
            }
          />
          <Route
            path={routes.createPerson}
            element={
              <RoleRoute allow={operational}>
                <CreatePerson />
              </RoleRoute>
            }
          />
          <Route
            path={routes.createOrganisation}
            element={
              <RoleRoute allow={operational}>
                <CreateOrganisation />
              </RoleRoute>
            }
          />

          <Route
            path={routes.quotes}
            element={
              <RoleRoute allow={operationalView}>
                <QuotesPipeline />
              </RoleRoute>
            }
          />
          <Route
            path="/quotes/combined/:quoteId"
            element={
              <RoleRoute allow={operationalView}>
                <CombinedQuoteBuilder />
              </RoleRoute>
            }
          />
          <Route
            path={routes.combinedQuoteBuilder}
            element={
              <RoleRoute allow={operational}>
                <CombinedQuoteBuilder />
              </RoleRoute>
            }
          />

          <Route
            path="/assets/:assetId"
            element={
              <RoleRoute allow={operational}>
                <AssetDetail />
              </RoleRoute>
            }
          />

          <Route
            path={routes.renewals}
            element={
              <RoleRoute allow={operationalView}>
                <RenewalsView />
              </RoleRoute>
            }
          />
          <Route
            path="/renewals/:renewalId"
            element={
              <RoleRoute allow={operationalView}>
                <RenewalDetail />
              </RoleRoute>
            }
          />
          <Route
            path="/renewals/:renewalId/amendment"
            element={
              <RoleRoute allow={operational}>
                <RenewalAmendment />
              </RoleRoute>
            }
          />

          <Route
            path={routes.policies}
            element={
              <RoleRoute allow={operationalView}>
                <PoliciesView />
              </RoleRoute>
            }
          />
          <Route
            path="/policies/:policyId"
            element={
              <RoleRoute allow={policyDetail} redirectTo={roleDeniedRedirect}>
                <PolicyDetail />
              </RoleRoute>
            }
          />
          <Route
            path="/policies/:policyId/history"
            element={
              <RoleRoute allow={operationalView}>
                <PolicyQuoteHistory />
              </RoleRoute>
            }
          />
          <Route
            path={routes.mtaWorkflow()}
            element={
              <RoleRoute allow={operational}>
                <MTAWorkflow />
              </RoleRoute>
            }
          />
          <Route
            path="/mta/:policyId"
            element={
              <RoleRoute allow={operational}>
                <MTAWorkflow />
              </RoleRoute>
            }
          />
          <Route
            path="/mta/:policyId/:mtaId"
            element={
              <RoleRoute allow={operational}>
                <MTAWorkflow />
              </RoleRoute>
            }
          />

          <Route
            path={routes.invoices}
            element={
              <RoleRoute allow={invoices} redirectTo={roleDeniedRedirect}>
                <InvoicesView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.reconciliationHistory}
            element={
              <RoleRoute allow={invoicePayments} redirectTo={routes.invoices}>
                <ReconciliationHistoryView />
              </RoleRoute>
            }
          />
          <Route
            path="/invoices/:invoiceId"
            element={
              <RoleRoute allow={invoices} redirectTo={roleDeniedRedirect}>
                <InvoiceDetail />
              </RoleRoute>
            }
          />

          <Route
            path={routes.reports}
            element={
              <RoleRoute allow={reports}>
                <ReportsView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.debtorsReport}
            element={
              <RoleRoute allow={reports}>
                <DebitorsReport />
              </RoleRoute>
            }
          />
          <Route
            path={routes.cashCreditsReport}
            element={
              <RoleRoute allow={reports}>
                <CashCreditsReportView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.businessWrittenReport}
            element={
              <RoleRoute allow={reports}>
                <BusinessWrittenReportView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.policyRenewalReport}
            element={
              <RoleRoute allow={reports}>
                <PolicyRenewalReportView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.commissionsReport}
            element={
              <RoleRoute allow={reports}>
                <CommissionsReportView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.adviserPerformanceReport}
            element={
              <RoleRoute allow={reports}>
                <AdviserPerformanceReport />
              </RoleRoute>
            }
          />
          <Route
            path={routes.bordeauxReport}
            element={
              <RoleRoute allow={reports}>
                <BordeauxReport />
              </RoleRoute>
            }
          />

          <Route
            path={routes.users}
            element={
              <RoleRoute allow={userManagement}>
                <UsersView />
              </RoleRoute>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <RoleRoute allow={userManagement}>
                <UserDetail />
              </RoleRoute>
            }
          />
          <Route
            path="/advisers/:adviserId"
            element={
              <RoleRoute allow={userManagement}>
                <AdviserDetailView />
              </RoleRoute>
            }
          />
          <Route
            path="/business-admins/:businessAdminId"
            element={
              <RoleRoute allow={userManagement}>
                <BusinessAdminDetailView />
              </RoleRoute>
            }
          />
          <Route
            path={routes.createFap}
            element={
              <RoleRoute allow={userManagement}>
                <CreateFAP />
              </RoleRoute>
            }
          />
          <Route
            path="/faps/:fapId/edit"
            element={
              <RoleRoute allow={userManagement}>
                <EditFAP />
              </RoleRoute>
            }
          />
          <Route
            path="/faps/:fapId"
            element={
              <RoleRoute allow={userManagement}>
                <FAPDetail />
              </RoleRoute>
            }
          />

          <Route
            path={routes.auditLog}
            element={
              <RoleRoute allow={userManagement}>
                <AuditLogView />
              </RoleRoute>
            }
          />

          <Route path={routes.notifications} element={<NotificationsPage />} />
          <Route
            path={routes.health}
            element={
              currentUser?.role === "SUPER_ADMIN" ? (
                <HealthPage />
              ) : (
                <Navigate to={routes.dashboard} replace />
              )
            }
          />
          <Route
            path={routes.appConfig}
            element={
              currentUser?.role === "SUPER_ADMIN" ? (
                <AppConfigPage />
              ) : (
                <Navigate to={routes.dashboard} replace />
              )
            }
          />
          <Route path={routes.eofferDemo} element={<EOfferDemo />} />

          <Route path="*" element={<Navigate to={routes.dashboard} replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default function App() {
  return (
    <AppProvider>
      <IdleSessionWatcher />
      <AppContent />
      <Toaster />
    </AppProvider>
  );
}
